import cx from "clsx";
import type { ReactNode } from "react";
import React from "react";
import { Portal } from "react-overlays";
import type { DOMContainer } from "react-overlays/useWaitForDOMRef";
import { usePopper } from "react-popper";
import { DropdownContext } from "~/components/common/parts/dropdown/Dropdown";
import { useClickAway } from "~/hooks/useClickAway";
import Styles from "./SimpleDropdown.module.scss";

type Props = {
	children: ReactNode;
	className?: string;
	disabled?: boolean;
	openClassName?: string;
	openLock?: boolean;
	// clickAwayで閉じない
	placement?: "bottom-start" | "bottom-end" | "top-start" | "top-end";
	title: string | ReactNode;
};

const InnerSimpleDropdown: React.FC<Props> = ({
	title,
	children,
	disabled,
	className = "",
	openClassName = "",
	openLock = false,
	placement
}: Props) => {
	const [show, setShow] = React.useState<boolean>(false);
	const referenceRef = React.useRef<HTMLButtonElement | null>(null);
	const popperRef = React.useRef<HTMLDivElement | null>(null);
	useClickAway(popperRef, referenceRef, (): void => {
		if (!openLock) {
			setShow(false);
		}
	});
	const { styles, attributes, update } = usePopper(
		referenceRef.current,
		popperRef.current,
		{
			placement
		}
	);
	React.useEffect(() => {
		if (update) update().then();
	}, [show, update]);
	const onClickToggle = (): void => {
		setShow(!show);
	};
	const providerValue = React.useMemo(
		() => ({ setShow, show }),
		[show, setShow]
	);
	return (
		<DropdownContext.Provider value={providerValue}>
			<button
				aria-expanded="false"
				aria-haspopup="true"
				className={cx(className, Styles.DropdownToggle, show && openClassName)}
				data-toggle="dropdown"
				disabled={disabled}
				onClick={onClickToggle}
				ref={referenceRef}
				type="button"
			>
				<span className={Styles.DropdownTitle}>{title}</span>
			</button>
			<Portal container={document.body}>
				<div
					className={cx(Styles.DropdownPopper, !show && Styles.Hide)}
					ref={popperRef}
					style={styles.popper}
					{...attributes.popper}
				>
					<div className={show ? "" : Styles.Hide}>{children}</div>
				</div>
			</Portal>
		</DropdownContext.Provider>
	);
};

export const SimpleDropdown: React.FC<Props> = ({
	title,
	children,
	disabled,
	className = "",
	openClassName = "",
	openLock = false,
	placement
}: Props) => {
	const [container, setContainer] = React.useState<DOMContainer | null>(null);
	React.useEffect(() => {
		setContainer(document.body);
	}, []);
	if (container === null) {
		return null;
	}
	return (
		<InnerSimpleDropdown
			className={className}
			disabled={disabled}
			openClassName={openClassName}
			openLock={openLock}
			placement={placement}
			title={title}
		>
			{children}
		</InnerSimpleDropdown>
	);
};
